import { useRef, useState } from "react";
import JSZip from "jszip";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileArchive, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ALLOWED = new Set(["pdf", "jpg", "jpeg", "png", "webp", "gif", "heic", "doc", "docx",
  "xls", "xlsx", "ppt", "pptx", "odt", "ods", "odp", "rtf", "txt", "csv", "msg", "eml"]);
const JUNK = /(^|\/)(__MACOSX|\.DS_Store|Thumbs\.db|desktop\.ini)(\/|$)|(^|\/)\._/i;
const MAX_BYTES = 25 * 1024 * 1024;

const extOf = (n) => (n.includes(".") ? n.split(".").pop().toLowerCase() : "");

// Regroupe les fichiers du ZIP par dossier (chemin complet « A / B »).
const groupEntries = (zip, rootName) => {
  const groups = {};
  let skipped = 0;
  zip.forEach((path, entry) => {
    if (entry.dir || JUNK.test(path)) return;
    const parts = path.split("/").filter(Boolean);
    const name = parts.pop();
    if (!ALLOWED.has(extOf(name))) { skipped += 1; return; }
    const folder = parts.length ? parts.join(" / ") : rootName;
    (groups[folder] = groups[folder] || []).push({ name, entry });
  });
  return { groups, skipped };
};

export const ZipImportButton = ({ contractId, folders, onDone }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const ensureFolder = async (name, known) => {
    const key = name.trim().toLowerCase();
    if (known[key]) return known[key];
    const { data } = await api.post(`/contracts/${contractId}/folders`, { name });
    known[key] = data.folder.id;
    return data.folder.id;
  };

  const run = async (file) => {
    let zip;
    try { zip = await JSZip.loadAsync(file); }
    catch { toast.error("ZIP illisible ou corrompu"); return; }
    const rootName = file.name.replace(/\.zip$/i, "");
    const { groups, skipped } = groupEntries(zip, rootName);
    const total = Object.values(groups).reduce((s, g) => s + g.length, 0);
    if (!total) { toast.error("Aucun fichier compatible dans ce ZIP"); return; }
    const known = Object.fromEntries((folders || []).map((f) => [f.name.trim().toLowerCase(), f.id]));
    let done = 0, ok = 0, failed = 0;
    setProgress({ done, total });
    for (const [folderName, items] of Object.entries(groups)) {
      let fid;
      try { fid = await ensureFolder(folderName, known); }
      catch (e) { toast.error(`${folderName} : ${apiError(e)}`); failed += items.length; done += items.length; continue; }
      for (const { name, entry } of items) {
        try {
          const blob = await entry.async("blob");
          if (blob.size > MAX_BYTES) throw new Error("dépasse 25 Mo");
          const fd = new FormData();
          fd.append("folder_id", fid);
          fd.append("file", new File([blob], name));
          await api.post(`/contracts/${contractId}/documents/upload`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
          ok += 1;
        } catch (e) { failed += 1; toast.error(`${name} : ${e.message?.includes("25 Mo") ? e.message : apiError(e)}`); }
        done += 1;
        setProgress({ done, total });
      }
    }
    setProgress(null);
    toast.success(`${ok} fichier(s) importé(s) dans ${Object.keys(groups).length} dossier(s)`
      + (failed ? ` · ${failed} en échec` : "") + (skipped ? ` · ${skipped} ignoré(s) (format non supporté)` : ""));
    onDone();
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".zip,application/zip" className="hidden"
             data-testid="contract-zip-input"
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) run(f); }} />
      <Button variant="outline" size="sm" disabled={!!progress}
              onClick={() => inputRef.current?.click()} data-testid="contract-zip-import-btn">
        {progress ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileArchive className="h-3.5 w-3.5 mr-1" />}
        {progress ? `Import ${progress.done}/${progress.total}…` : "Importer un ZIP"}
      </Button>
    </>
  );
};
